import { DrawerBody, DrawerFooter, Button, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { DrawerContext } from "components/drawer";
import { AuthContext } from "contexts/AuthContext";
import { getAll, updateMany } from "lib/firebase";
import { isCompositeBroadcast } from "lib/playlist";
import { planContentMembership } from "lib/playlistChange";
import { sortByLabel } from "lib/sort";
import { useContext, useEffect, useMemo, useState } from "react";
import BroadcastTicks from "views/modal/content-broadcasts-list";

// Where one content item is played, and a way to change it: every regular broadcast is a line
// with a tick. A ticked broadcast plays the content; unticking removes it (every time it appears
// in the list), ticking a new one adds it as the LAST item of that broadcast. Saving writes
// everything in ONE update, with the durations of the broadcasts, their multiplier copies and
// the composite broadcasts built on them recalculated.
export default function ContentBroadcasts({ content, onChange }) {
  const { user } = useContext(AuthContext)
  const { closeDrawer } = useContext(DrawerContext)
  const toast = useToast()

  // Fresh from the database: the lists held by the app are cached and can be out of date
  const [state, setState] = useState({ loading: true, broadcasts: [], error: null })
  const [ticked, setTicked] = useState(new Set())
  const [initial, setInitial] = useState(new Set())
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let active = true
    getAll("broadcast", user.clientId)
      .then(broadcasts => {
        if (!active) return
        const regular = broadcasts.filter(b => !b.sourceId && !isCompositeBroadcast(b))
        const holding = new Set(regular.filter(b => (b.contentList || []).includes(content.id)).map(b => b.id))
        setState({ loading: false, broadcasts, error: null })
        setInitial(holding)
        setTicked(new Set(holding))
      })
      .catch(err => active && setState({ loading: false, broadcasts: [], error: err.message || String(err) }))
    return () => { active = false }
  }, [user.clientId, content.id])

  const regularBroadcasts = useMemo(
    () => sortByLabel(state.broadcasts.filter(b => !b.sourceId && !isCompositeBroadcast(b)).map(b => ({ ...b, label: b.name || "" }))),
    [state.broadcasts]
  )

  const toAdd = [...ticked].filter(id => !initial.has(id))
  const toRemove = [...initial].filter(id => !ticked.has(id))
  const changes = toAdd.length + toRemove.length

  const toggle = (id) => setTicked(current => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const save = async () => {
    if (saving || changes === 0) return
    setSaving(true)
    try {
      // The data is read again right before writing, so a change made meanwhile is not lost
      const [broadcasts, contentItems] = await Promise.all([getAll("broadcast", user.clientId), getAll("content", user.clientId)])
      const plan = planContentMembership({ broadcasts, contentItems, contentId: content.id, add: toAdd, remove: toRemove })
      if (Object.keys(plan.updates).length > 0) {
        await updateMany(plan.updates)
      }
      onChange && onChange()
      closeDrawer()
      toast({ title: `Content added to ${plan.added.length}, removed from ${plan.removed.length} broadcast(s)`, status: "success", duration: null, isClosable: true })
    } catch (err) {
      toast({ title: "Error!", description: err.message || String(err), status: "error", duration: null, isClosable: true })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <DrawerBody>
        {state.loading ? (
          <Flex justify="center" pt={8}><Spinner /></Flex>
        ) : state.error ? (
          <Text color="red.500">{state.error}</Text>
        ) : (
          <BroadcastTicks content={content} broadcasts={regularBroadcasts} initial={initial} ticked={ticked} saving={saving} onToggle={toggle} changes={changes} />
        )}
      </DrawerBody>
      <DrawerFooter>
        <Button colorScheme="brand" borderRadius="30px" me="10px" isLoading={saving} isDisabled={state.loading || !!state.error || changes === 0} onClick={save}>
          Save
        </Button>
      </DrawerFooter>
    </>
  )
}
